// Lightweight, namespaced localStorage helpers. No accounts — progress lives
// only in the browser so a returning user resumes where they left off.

const NS = "pib";

function key(...parts) {
  return [NS, ...parts].join(".");
}

function read(k, fallback) {
  try {
    const raw = localStorage.getItem(k);
    return raw == null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

function write(k, value) {
  try {
    localStorage.setItem(k, JSON.stringify(value));
    // notify same-tab listeners
    window.dispatchEvent(new CustomEvent("pib-storage", { detail: { key: k } }));
  } catch (e) {
    /* ignore quota / disabled storage */
  }
}

// ---- Role -----------------------------------------------------------------
export const ROLE_KEY = key("role");
export function getRole() {
  return read(ROLE_KEY, null);
}
export function setRole(role) {
  write(ROLE_KEY, role);
}

// ---- Organizer phase checklists -----------------------------------------
// Stored as { [index]: true } per phase.
function checklistKey(slug, mode, phaseKey) {
  return key("checklist", slug, mode, phaseKey);
}
export function getChecklist(slug, mode, phaseKey) {
  return read(checklistKey(slug, mode, phaseKey), {});
}
export function setChecklistItem(slug, mode, phaseKey, index, checked) {
  const current = getChecklist(slug, mode, phaseKey);
  const next = { ...current };
  if (checked) next[index] = true;
  else delete next[index];
  write(checklistKey(slug, mode, phaseKey), next);
  return next;
}

// ---- Visited phases (for phases without checklists) -----------------------
function visitedKey(slug, mode) {
  return key("visited", slug, mode);
}
export function getVisited(slug, mode) {
  return read(visitedKey(slug, mode), {});
}
export function markVisited(slug, mode, phaseKey) {
  const current = getVisited(slug, mode);
  if (current[phaseKey]) return current;
  const next = { ...current, [phaseKey]: true };
  write(visitedKey(slug, mode), next);
  return next;
}

// ---- Volunteer flow -------------------------------------------------------
function volunteerKey(slug) {
  return key("volunteer", slug);
}
export function getVolunteerProgress(slug) {
  return read(volunteerKey(slug), { stepIndex: 0, done: {}, gathered: {}, quality: {}, completedKits: 0, screen: null });
}
export function setVolunteerProgress(slug, progress) {
  write(volunteerKey(slug), progress);
}

// ---- Kit counter (all-time total, per activity, never reset daily) ---------
function kitCountKey(slug) {
  return key("kits", slug);
}
export function getKitCount(slug) {
  return read(kitCountKey(slug), 0);
}
export function incrementKitCount(slug) {
  const next = (getKitCount(slug) || 0) + 1;
  write(kitCountKey(slug), next);
  return next;
}
export function resetKitCount(slug) {
  localStorage.removeItem(kitCountKey(slug));
}

// ---- Reset ----------------------------------------------------------------
export function resetActivityMode(slug, mode) {
  const prefix = key("checklist", slug, mode);
  const visited = visitedKey(slug, mode);
  Object.keys(localStorage)
    .filter((k) => k.startsWith(prefix) || k === visited)
    .forEach((k) => localStorage.removeItem(k));
  window.dispatchEvent(new CustomEvent("pib-storage", { detail: { key: prefix } }));
}
export function resetVolunteer(slug) {
  localStorage.removeItem(volunteerKey(slug));
  window.dispatchEvent(new CustomEvent("pib-storage", { detail: { key: volunteerKey(slug) } }));
}
